import type { MeetingActionItem } from '../types';
import { canonicalizeStatus } from './actionItemStatus';
import { monthOf, runLabel } from './reviewMonths';

/**
 * Derived fields for the action-items grid (Christian, 2026-10-28). Pure, and each takes `now` so a test pins the
 * clock instead of letting the calendar in.
 */

/** A review period as months: `Jan – Sep 2026`. Null when either bound is missing or unreadable. */
export function periodMonthsLabel(start: string | null | undefined, end: string | null | undefined): string | null {
  const s = monthOf(start);
  const e = monthOf(end);
  return s && e ? runLabel(s, e) : null;
}

/** The default title of a new action item: `<client> · <period>`, e.g. `Acme LLC · Jan – Sep 2026`. The user can override it. */
export function defaultActionItemTitle(
  clientName: string | null | undefined,
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined,
): string {
  const parts = [clientName?.trim() || null, periodMonthsLabel(periodStart, periodEnd)].filter(Boolean);
  return parts.join(' · ');
}

const DAY_MS = 86_400_000;

/**
 * Whole days the item has been open: creation to completion for a closed item, creation to `now` for an open
 * one. Null when there is no readable creation time, or when a closed item never recorded when it closed (a
 * guessed age is worse than a blank cell).
 */
export function daysOpen(item: Pick<MeetingActionItem, 'createdAt' | 'completedAt' | 'status'>, now: Date = new Date()): number | null {
  const created = item.createdAt ? Date.parse(item.createdAt) : NaN;
  if (Number.isNaN(created)) return null;
  const status = canonicalizeStatus(item.status);
  const closed = status === 'completed' || status === 'cancelled';
  let end = now.getTime();
  if (closed) {
    const completed = item.completedAt ? Date.parse(item.completedAt) : NaN;
    if (Number.isNaN(completed)) return null;
    end = completed;
  }
  return Math.max(0, Math.floor((end - created) / DAY_MS));
}
